import { useState, useCallback, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { searchGlobal, ISearchParams, ISearchResult } from '../api/search';
import UserContext from './UserContext';

export type SearchContext = 'recipe' | 'batch' | 'global';

interface UseAISearchReturn {
  results: ISearchResult[];
  loading: boolean;
  error: string | null;
  total: number;
  page: number;
  search: (query: string, options?: Partial<ISearchParams>) => Promise<void>;
  clearResults: () => void;
  getContextFromLocation: () => SearchContext;
  isAuthenticated: boolean;
}

export const useAISearch = (): UseAISearchReturn => {
  const [results, setResults] = useState<ISearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const location = useLocation();
  const { user } = useContext(UserContext);
  const isAuthenticated = !!user;

  // Determine search context based on current route
  const getContextFromLocation = useCallback((): SearchContext => {
    if (location.pathname.startsWith('/les_recettes')) {
      return 'recipe';
    } else if (location.pathname.startsWith('/mes_batchs')) {
      return 'batch';
    }
    return 'global';
  }, [location.pathname]);

  // Main search function
  const search = useCallback(async (query: string, options?: Partial<ISearchParams>) => {
    if (!query || query.trim().length === 0) {
      setResults([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const context = getContextFromLocation();

      // Build search parameters based on context
      const params: ISearchParams = {
        q: query.trim(),
        type: context === 'global' ? 'all' : context,
        status: isAuthenticated ? 'all' : 'published',
        page: 1,
        limit: 10,
        sort: 'relevance',
        ...options
      };

      console.log('🔍 Search params:', {
        isAuthenticated,
        user: user ? 'logged in' : 'anonymous',
        params
      });

      const response = await searchGlobal(params);

      setResults(response.results);
      setTotal(response.total);
      setPage(response.page);
    } catch (err) {
      console.error('Search failed:', err);
      setError('Failed to perform search. Please try again.');
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [getContextFromLocation, isAuthenticated, user]);

  // Clear search results
  const clearResults = useCallback(() => {
    setResults([]);
    setTotal(0);
    setPage(1);
    setError(null);
  }, []);

  return {
    results,
    loading,
    error,
    total,
    page,
    search,
    clearResults,
    getContextFromLocation,
    isAuthenticated
  };
};
